import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import baseSway from "../../../../media/base-sway-unscreen.gif";
import celebration from "../../../../media/celebration-unscreen.gif";
import {
    normalizeSlotsToArray,
    getMixedSlotDisplayValue,
    isBlankBoxCorrectInContext,
    getBlankBoxLiveStatus,
    getSlotLiveStatus,
} from "../../../../utils/quizResultsUtils";
import './TutorSingleQuestionPage.css';

function TutorSingleQuestionPage({ quizQuestion, onAnswerSelect, ButtonsAreClickable, studentInput, isFocused = false }) {
    //console.log('studentInput in TutorSingleQuestionPage',studentInput);
    // Always call hooks at the top level
    const [selectedAnswer, setSelectedAnswer] = useState("select");
    const [FreeTextInputValue, setFreeTextInputValue] = useState("");
    const [currentVideoSource, setCurrentVideoSource] = useState(baseSway);
    const [correctFeedback, setCorrectFeedback] = useState(false);
    const [incorrectFeedback, setIncorrectFeedback] = useState(false);
    const [buttonsAreClickable, setButtonsAreClickable] = useState(false);
    const [blankBoxAnswers, setBlankBoxAnswers] = useState({});
    const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
    const [droppedLabels, setDroppedLabels] = useState({});
    const [shuffledLabels, setShuffledLabels] = useState([]);
    const [nextBoxId, setNextBoxId] = useState(null);
    const [mixedSlotAnswers, setMixedSlotAnswers] = useState({});

    /* Phase 5: hover popover showing correct answer for tutor */
    const [hoveredBox, setHoveredBox] = useState(null);
    const hoverTimeoutRef = useRef(null);
    const HOVER_DELAY_MS = 300;

    const getMixedSlotCorrectAnswer = (slot) => {
        if (slot.slotType === 'blank-box') return slot.answer ?? '';
        if (slot.slotType === 'drag-and-drop') return slot.label ?? '';
        if ((slot.slotType === 'multiple-choice' || slot.slotType === 'dropdown') && slot.options?.length) {
            const opt = slot.options.find((o) => o.id === slot.correctAnswerId);
            return opt ? opt.text : String(slot.correctAnswerId ?? '');
        }
        return '';
    };

    const handleBoxMouseEnter = (e, id, answerText, studentAnswer = '', extras = null) => {
        const target = e.currentTarget;
        const rect = target.getBoundingClientRect();
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredBox({
                id: String(id),
                answer: answerText ?? '',
                studentAnswer: studentAnswer ?? '',
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
                // Option list for dropdown / multiple-choice slots, so the
                // tutor can see the full set of choices, not just the one
                // the student picked.
                options: extras?.options ?? null,
                correctOptionId: extras?.correctOptionId ?? null,
                studentOptionId: extras?.studentOptionId ?? null,
            });
        }, HOVER_DELAY_MS);
    };

    const handleBoxMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setHoveredBox(null);
    };

    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        };
    }, []);

    // Reset states when question changes
    useEffect(() => {

        setSelectedAnswer("select");
        setFreeTextInputValue("");
        setCurrentVideoSource(baseSway);
        setCorrectFeedback(false);
        setIncorrectFeedback(false);
        setButtonsAreClickable(false);
        setBlankBoxAnswers({});
        setDroppedLabels({});
        setMixedSlotAnswers({});
    }, [quizQuestion]);  // This will run whenever quizQuestion changes

    // this useEffect is to re-render everything in the case that we have consecutive quiz questions being called from the parent of
    // this component to ensure all state is reset


    useEffect(() => {
        if (quizQuestion && quizQuestion.questionType === 'Drag-And-Drop-Question') {
            const labels = quizQuestion.dragBoxes.map(box => box.label);
            setShuffledLabels([...labels].sort(() => Math.random() - 0.5));
            // Set initial next box (first by order; highlight is also derived in render via dragNextBoxId)
            const sorted = quizQuestion.dragBoxes.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const firstBox = sorted[0];
            if (firstBox) {
                setNextBoxId(firstBox.id);
            }
        }
    }, [quizQuestion]);

    // Mirror student input into the tutor's read-only view.
    // Guard: if the message carries a questionId that doesn't match the question
    // currently shown to the tutor, discard it. This prevents stale input from Q1
    // (which arrives briefly before studentInputStates is cleared) from stamping
    // incorrect-feedback styling onto Q2 the moment the tutor advances.
    // The guard is backward-compatible: if questionId is absent (older client),
    // the message is accepted as before.
    useEffect(() => {
        if (!studentInput) return;

        if (studentInput.questionId && studentInput.questionId !== quizQuestion?._id) return;

        if (studentInput.isSubmitted) {
            if (studentInput.isCorrect) {
                setCorrectFeedback(true);
            } else {
                setIncorrectFeedback(true);
            }
        }

        switch (quizQuestion?.questionType) {
            case 'Multiple-Choice-Question':
                // Set state directly instead of calling handleButtonClick, which is
                // defined after the early-return guard and would also trigger
                // onAnswerSelect (a no-op) plus animation side-effects the tutor
                // view should never run.
                setSelectedAnswer(studentInput.input?.id ?? 'select');
                break;
            case 'Drop-Down-List':
                setSelectedAnswer(studentInput.input);
                break;
            case 'Free-Text-Question':
                setFreeTextInputValue(studentInput.input);
                break;
            case 'Blank-Box-Question':
                setBlankBoxAnswers(studentInput.input);
                break;
            case 'Drag-And-Drop-Question': {
                const input = studentInput.input || {};
                setDroppedLabels(input);
                const placedLabels = Object.values(input);
                const allLabels = quizQuestion.dragBoxes.map((box) => box.label);
                const used = [...placedLabels];
                const remainingLabels = allLabels.filter((label) => {
                    const idx = used.indexOf(label);
                    if (idx === -1) return true;
                    used.splice(idx, 1);
                    return false;
                });
                setShuffledLabels(remainingLabels);
                break;
            }
            case 'Mixed':
                setMixedSlotAnswers(studentInput.input || {});
                break;
            default:
                break;
        }
    }, [studentInput, quizQuestion]);

    // Next empty drag box by order (so tutor view highlights the same slot as the student)
    const dragNextBoxId =
        quizQuestion?.questionType === 'Drag-And-Drop-Question' && quizQuestion.dragBoxes?.length
            ? (() => {
                  const filledIds = Object.keys(droppedLabels);
                  const next = quizQuestion.dragBoxes
                      .filter((b) => !filledIds.includes(b.id))
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
                  return next?.id ?? null;
              })()
            : null;

    const removeOneLabel = (labels, target) => {
        const idx = labels.indexOf(target);
        if (idx === -1) return labels;
        return [...labels.slice(0, idx), ...labels.slice(idx + 1)];
    };

    // Now conditionally render if quizQuestion is null
    if (!quizQuestion) {
        return (
            <div className="flex justify-center items-center">
                Questions completed! The child should now be directed to the next lesson.
            </div>
        );
    }

    const handleButtonClick = (answer) => {
        //console.log(answer);
        setSelectedAnswer(answer.id);
        const isCorrect = answer.id === quizQuestion.correctAnswer;
        
        if (isCorrect) {
            setCorrectFeedback(true);
        } else {
            setIncorrectFeedback(true);
        }

        onAnswerSelect(isCorrect, answer.id);
        if (isCorrect) {
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        }
        setButtonsAreClickable(false);
    };

    const handleFreeTextSubmit = () => {
        const lowerCaseTexts = quizQuestion.answers.map(answer => answer.text.toLowerCase().trim());
        const isCorrect = lowerCaseTexts.includes(FreeTextInputValue.toLowerCase().trim());

        if (isCorrect) {
            setCorrectFeedback(true);
        } else {
            setIncorrectFeedback(true);
        }

        onAnswerSelect(isCorrect, FreeTextInputValue);
        if (isCorrect) {
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        }
        setButtonsAreClickable(false);
    };

    const handleDropDownSubmit = () => {
        if (!quizQuestion.answers.some(answer => answer.id === selectedAnswer)) {
            return;
        }

        const isCorrect = selectedAnswer === quizQuestion.correctAnswer;

        if (isCorrect) {
            setCorrectFeedback(true);
        } else {
            setIncorrectFeedback(true);
        }

        onAnswerSelect(isCorrect, selectedAnswer);
        if (isCorrect) {
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        }
        setButtonsAreClickable(false);
    };

    const ButtonColour = (selectedAnswer, answer) => {
        let Formatting = 'whitespace-nowrap h-12 px-2 my-2 text-lg hover:bg-nova-light-green text-nova-purple border border-2 border-nova-purple rounded-lg focus:outline-none';

        let AdditionalFormatting = ''

        if (selectedAnswer === answer.id && answer.id === quizQuestion.correctAnswer) {
            AdditionalFormatting += ' bg-nova-green';
        } else if (selectedAnswer === answer.id && answer.id !== quizQuestion.correctAnswer) {
            AdditionalFormatting += ' bg-nova-coral text-white';
        } else {
            AdditionalFormatting += ' bg-nova-cream';
        }

        Formatting += AdditionalFormatting;

        return Formatting;
    };

    const handleBlankBoxInput = (boxId, value) => {
        setBlankBoxAnswers(prev => ({
            ...prev,
            [boxId]: value
        }));
    };

    const handleBlankBoxKeyPress = (e, currentBox) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const nextBox = quizQuestion.blankBoxes.find(box => box.order === currentBox.order + 1);
            if (nextBox) {
                setCurrentBoxIndex(currentBox.order);
                document.getElementById(`box-${nextBox.id}`).focus();
            } else {
                handleBlankBoxSubmit();
            }
        }
    };

    const handleBlankBoxSubmit = () => {
        // Link-group aware correctness: boxes sharing a linkGroupId are judged
        // as a multiset, so commutative answers (e.g. 19*5 and 5*19) both pass.
        const isCorrect = quizQuestion.blankBoxes.every(box =>
            isBlankBoxCorrectInContext(box, quizQuestion.blankBoxes, blankBoxAnswers)
        );

        if (isCorrect) {
            setCorrectFeedback(true);
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        } else {
            setIncorrectFeedback(true);
        }

        onAnswerSelect(isCorrect, allAnswers);
        setButtonsAreClickable(false);
    };

    const handleLabelClick = (label) => {
        if (!buttonsAreClickable) return;
        
        // Find the next empty box by order
        const filledBoxIds = Object.keys(droppedLabels);
        const nextBox = quizQuestion.dragBoxes
            .filter(box => !filledBoxIds.includes(box.id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];

        if (nextBox) {
            // Update droppedLabels with the new label
            setDroppedLabels(prev => ({
                ...prev,
                [nextBox.id]: label
            }));

            // Update shuffledLabels
            setShuffledLabels(prev => removeOneLabel(prev, label));

            // Set next box to highlight
            const nextEmptyBox = quizQuestion.dragBoxes
                .filter(box => !filledBoxIds.includes(box.id) && box.id !== nextBox.id)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
            setNextBoxId(nextEmptyBox?.id || null);
        }
    };

    const handleDragBoxSubmit = () => {
        const isCorrect = quizQuestion.dragBoxes.every(box => 
            droppedLabels[box.id] === box.label
        );
        
        if (isCorrect) {
            setCorrectFeedback(true);
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        } else {
            setIncorrectFeedback(true);
        }
        
        onAnswerSelect(isCorrect, droppedLabels);
        setButtonsAreClickable(false);
    };

    // Add handleReset function
    const handleReset = () => {
        // Reset droppedLabels to empty
        setDroppedLabels({});
        // Re-shuffle and restore all labels
        const labels = quizQuestion.dragBoxes.map(box => box.label);
        setShuffledLabels([...labels].sort(() => Math.random() - 0.5));
        // Reset nextBoxId to the first box by order
        const sorted = quizQuestion.dragBoxes.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const firstBox = sorted[0];
        if (firstBox) {
            setNextBoxId(firstBox.id);
        }
    };

    return (
        <>
        <div className="flex flex-col items-center h-full w-full">
            <div className="flex flex-col items-center w-full">
                {/* Add an indicator when showing an explanation question */}
                {quizQuestion?.isAnswerExplanation && (
                    <div className="w-full text-center bg-nova-light-purple p-2 mb-2 rounded">
                        <p className="text-nova-purple font-medium">
                            Student is answering an explanation question
                        </p>
                    </div>
                )}
                
                {/* Only show this div if there's an image */}
                {quizQuestion.image && quizQuestion.image.trim() !== '' ? (
                    // removed border border-red-500 from div below as no longer testing
                    <div className="w-full text-center text-2xl font-bold tutor-question-page-title">
                        <p>{quizQuestion.question}</p>
                    </div>
                ) : null}

                {/* This div will either contain the image or the question text - vh when focused to match student, % when not */}
                {quizQuestion.questionType !== 'Blank-Box-Question' && quizQuestion.questionType !== 'Drag-And-Drop-Question' && quizQuestion.questionType !== 'Mixed' && (
                    <div className={`w-full flex justify-center items-center ${isFocused ? 'h-[52vh]' : 'h-[30%]'}`}>
                        {quizQuestion.image && quizQuestion.image.trim() !== '' ? (
                            <img src={quizQuestion.image} alt="Question" className="max-w-full max-h-full object-contain" />
                        ) : (
                            <p className="tutor-question-page-title text-2xl font-bold text-center px-4">{quizQuestion.question}</p>
                        )}
                    </div>
                )}

                {correctFeedback && (
                    <div className="w-full text-center border-2 border-green-600 rounded-lg correct-feedback">
                        <p>Correct!</p>
                        {quizQuestion.correctFeedback && <p>{quizQuestion.correctFeedback}</p>}
                    </div>
                )}      
                                 
                {incorrectFeedback && (
                    <div className="w-full text-center border-2 border-red-600 rounded-lg incorrect-feedback">
                        <p>Incorrect!</p>
                        {quizQuestion.incorrectFeedback && <p>{quizQuestion.incorrectFeedback}</p>}
                    </div>
                )}

              {quizQuestion.questionType === 'Multiple-Choice-Question' ? (
                    <div className="w-full flex flex-col gap-3 z-20">
                        {quizQuestion.answers.map((answer) => (
                            <button
                                key={answer.id}
                                type="button"
                                onClick={buttonsAreClickable ? () => handleButtonClick(answer) : undefined}
                                className={`quiz-option-card ${selectedAnswer === answer.id ? 'quiz-option-selected' : ''} ${selectedAnswer === answer.id && answer.id === quizQuestion.correctAnswer ? '!border-nova-green !bg-nova-light-green' : ''} ${selectedAnswer === answer.id && answer.id !== quizQuestion.correctAnswer ? '!border-nova-coral' : ''}`}
                            >
                                <span className="quiz-option-dot">
                                    {selectedAnswer === answer.id && <span className="quiz-option-dot-inner" />}
                                </span>
                                <span className="quiz-option-label">{answer.text}</span>
                            </button>
                        ))}
                    </div>
                ) : quizQuestion.questionType === 'Drop-Down-List' ? (
                    <main className="drop-down-container w-full">
                        <div className="quiz-select-wrapper w-full my-2">
                            <select
                                value={selectedAnswer}
                                onChange={(e) => { setSelectedAnswer(e.target.value); }}
                                className="quiz-select quiz-focus-ring w-full"
                                disabled={true}
                            >
                                <option value="select">Select Answer</option>
                                {quizQuestion.answers.map((answer) => (
                                    <option key={answer.id} value={answer.id}>
                                        {answer.text}
                                    </option>
                                ))}
                            </select>
                            <span className="quiz-select-chevron" aria-hidden>▾</span>
                        </div>
                        <button
                            type="button"
                            onClick={buttonsAreClickable ? handleDropDownSubmit : undefined}
                            className="quiz-submit-btn mt-4 focus:outline-none"
                        >
                            Submit
                        </button>
                    </main>
                ) : quizQuestion.questionType === 'Free-Text-Question' ? (
                    <div className="w-full flex flex-col items-center justify-center my-2">
                        <input
                            type="text"
                            value={FreeTextInputValue}
                            onChange={(e) => { setFreeTextInputValue(e.target.value); }}
                            placeholder="Enter answer here"
                            className="quiz-text-input quiz-focus-ring w-full max-w-md"
                            disabled={true}
                        />
                        <button
                            type="button"
                            onClick={buttonsAreClickable ? handleFreeTextSubmit : undefined}
                            className="quiz-submit-btn w-32 mt-4 focus:outline-none"
                        >
                            Submit
                        </button>
                    </div>
                ) : quizQuestion.questionType === 'Blank-Box-Question' ? (
                    isFocused ? (
                    <div className="relative flex justify-center items-center">
                            <img 
                                src={quizQuestion.image} 
                                alt="Question" 
                                className="object-contain max-h-[60vh] max-w-full"
                                draggable="false"
                            />
                            {quizQuestion.blankBoxes.map((box) => (
                                <div
                                    key={box.id}
                                    className="absolute tutor-question-blank-box-wrapper"
                                    style={{
                                        left: `${box.x}%`,
                                        top: `${box.y}%`,
                                        width: `${box.width}%`,
                                        height: `${box.height}%`
                                    }}
                                    onMouseEnter={(e) => handleBoxMouseEnter(e, box.id, box.answer, blankBoxAnswers[box.id])}
                                    onMouseLeave={handleBoxMouseLeave}
                                >
                                    {(() => {
                                        const liveStatus = getBlankBoxLiveStatus(box, quizQuestion.blankBoxes, blankBoxAnswers);
                                        return (
                                            <input
                                                id={`box-${box.id}`}
                                                type="text"
                                                value={blankBoxAnswers[box.id] || ''}
                                                onChange={(e) => handleBlankBoxInput(box.id, e.target.value)}
                                                onKeyPress={(e) => handleBlankBoxKeyPress(e, box)}
                                                className={`w-full h-full p-1 border-2 tutor-question-blank-box-input ${
                                                    liveStatus !== 'neutral'
                                                        ? liveStatus === 'green'
                                                            ? 'border-green-500 bg-green-100'
                                                            : 'border-red-500 bg-red-100'
                                                        : 'border-gray-400 bg-gray-200'
                                                }`}
                                                disabled={true}
                                            />
                                        );
                                    })()}
                                </div>
                            ))}
                    </div>
                    ) : (
                    <div className="relative w-full h-[45%] flex justify-center items-center">
                            <div className="relative h-full w-full flex justify-center items-center">
                                <div className="relative h-full" style={{ maxWidth: '100%' }}>
                                    <img 
                                        src={quizQuestion.image} 
                                        alt="Question" 
                                        className="h-full w-auto object-contain max-w-full"
                                        draggable="false"
                                    />
                                    {quizQuestion.blankBoxes.map((box) => (
                                        <div
                                            key={box.id}
                                            className="absolute tutor-question-blank-box-wrapper"
                                            style={{
                                                left: `${box.x}%`,
                                                top: `${box.y}%`,
                                                width: `${box.width}%`,
                                                height: `${box.height}%`
                                            }}
                                            onMouseEnter={(e) => handleBoxMouseEnter(e, box.id, box.answer, blankBoxAnswers[box.id])}
                                            onMouseLeave={handleBoxMouseLeave}
                                        >
                                            {(() => {
                                                const liveStatus = getBlankBoxLiveStatus(box, quizQuestion.blankBoxes, blankBoxAnswers);
                                                return (
                                                    <input
                                                        id={`box-${box.id}`}
                                                        type="text"
                                                        value={blankBoxAnswers[box.id] || ''}
                                                        onChange={(e) => handleBlankBoxInput(box.id, e.target.value)}
                                                        onKeyPress={(e) => handleBlankBoxKeyPress(e, box)}
                                                        className={`w-full h-full p-1 border-2 tutor-question-blank-box-input ${
                                                            liveStatus !== 'neutral'
                                                                ? liveStatus === 'green'
                                                                    ? 'border-green-500 bg-green-100'
                                                                    : 'border-red-500 bg-red-100'
                                                                : 'border-gray-400 bg-gray-200'
                                                        }`}
                                                        disabled={true}
                                                    />
                                                );
                                            })()}
                                        </div>
                                    ))}
                                </div>
                            </div>
                    </div>
                    )
                ) : quizQuestion.questionType === 'Drag-And-Drop-Question' ? (
                    isFocused ? (
                    <div className="relative flex justify-center items-center">
                        <img 
                            src={quizQuestion.image} 
                            alt="Question" 
                            className="object-contain max-h-[60vh] max-w-full"
                            draggable="false"
                        />
                        {quizQuestion.dragBoxes.map((box) => (
                            <div
                                key={box.id}
                                className={`absolute tutor-question-drag-slot quiz-drop-zone ${droppedLabels[box.id] ? 'quiz-drop-zone-filled' : ''} ${
                                    droppedLabels[box.id]
                                        ? droppedLabels[box.id] === box.label
                                            ? '!border-nova-green !bg-nova-light-green'
                                            : '!border-nova-coral'
                                        : box.id === dragNextBoxId
                                            ? '!border-nova-mid-purple !bg-nova-light-purple'
                                            : '!border-[var(--nova-light-purple)]'
                                }`}
                                style={{
                                    left: `${box.x}%`,
                                    top: `${box.y}%`,
                                    width: `${box.width}%`,
                                    height: `${box.height}%`
                                }}
                                onMouseEnter={(e) => handleBoxMouseEnter(e, box.id, box.label, droppedLabels[box.id])}
                                onMouseLeave={handleBoxMouseLeave}
                            >
                                {droppedLabels[box.id] ? (
                                    <span className="quiz-drop-zone-label">{droppedLabels[box.id]}</span>
                                ) : null}
                            </div>
                        ))}
                    </div>
                    ) : (
                    <div className="relative w-full h-[45%] flex justify-center items-center ">
                        <div className="relative h-full w-full flex justify-center items-center">
                            <div className="relative h-full" style={{ maxWidth: '100%' }}>
                                <img 
                                    src={quizQuestion.image} 
                                    alt="Question" 
                                    className="h-full w-auto object-contain"
                                    draggable="false"
                                />
                                {quizQuestion.dragBoxes.map((box) => (
                                    <div
                                        key={box.id}
                                        className={`absolute tutor-question-drag-slot quiz-drop-zone ${droppedLabels[box.id] ? 'quiz-drop-zone-filled' : ''} ${
                                            droppedLabels[box.id]
                                                ? droppedLabels[box.id] === box.label
                                                    ? '!border-nova-green !bg-nova-light-green'
                                                    : '!border-nova-coral'
                                                : box.id === dragNextBoxId
                                                  ? '!border-nova-mid-purple !bg-nova-light-purple'
                                                  : '!border-[var(--nova-light-purple)]'
                                        }`}
                                        style={{
                                            left: `${box.x}%`,
                                            top: `${box.y}%`,
                                            width: `${box.width}%`,
                                            height: `${box.height}%`
                                        }}
                                        onMouseEnter={(e) => handleBoxMouseEnter(e, box.id, box.label, droppedLabels[box.id])}
                                        onMouseLeave={handleBoxMouseLeave}
                                    >
                                        {droppedLabels[box.id] ? (
                                            <span className="quiz-drop-zone-label">{droppedLabels[box.id]}</span>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    )
                ) : quizQuestion.questionType === 'Mixed' ? (
                    (() => {
                        const slots = normalizeSlotsToArray(quizQuestion.slots);
                        const withPosition = slots.filter((s) => s.x != null && s.y != null && s.width != null && s.height != null);
                        const renderMixedSlots = () =>
                            withPosition.map((slot) => {
                                const value = mixedSlotAnswers[slot.id] ?? '';
                                const display = getMixedSlotDisplayValue(slot, value);
                                // Per-slot live status (green/red/neutral). For linked groups this
                                // uses greedy multiset matching, so an individual slot can turn
                                // green as soon as its value is in the remaining pool — matching
                                // the student-side behaviour. isSlotCorrectInContext would only
                                // go green once the whole group is correct.
                                const liveStatus = getSlotLiveStatus(slot, slots, mixedSlotAnswers);
                                const correctAnswer = getMixedSlotCorrectAnswer(slot);
                                const hasOptionList =
                                    (slot.slotType === 'dropdown' || slot.slotType === 'multiple-choice') &&
                                    Array.isArray(slot.options) &&
                                    slot.options.length > 0;
                                const hoverExtras = hasOptionList
                                    ? {
                                        options: slot.options,
                                        correctOptionId: slot.correctAnswerId ?? null,
                                        studentOptionId: value || null,
                                    }
                                    : null;
                                return (
                                    <div
                                        key={slot.id}
                                        className={`absolute flex flex-col items-center justify-center p-1 border-2 tutor-question-blank-box-wrapper ${
                                            liveStatus === 'green'
                                                ? 'border-green-500 bg-green-100'
                                                : liveStatus === 'red'
                                                    ? 'border-red-500 bg-red-100'
                                                    : 'border-gray-400 bg-gray-200'
                                        }`}
                                        style={{
                                            left: `${slot.x}%`,
                                            top: `${slot.y}%`,
                                            width: `${slot.width}%`,
                                            height: `${slot.height}%`,
                                        }}
                                        onMouseEnter={(e) => handleBoxMouseEnter(e, slot.id, correctAnswer, display, hoverExtras)}
                                        onMouseLeave={handleBoxMouseLeave}
                                    >
                                        <span className="text-sm font-medium truncate w-full text-center">{display || '—'}</span>
                                    </div>
                                );
                            });
                        return isFocused ? (
                            <div className="relative flex justify-center items-center">
                                <img
                                    src={quizQuestion.image}
                                    alt="Question"
                                    className="object-contain max-h-[60vh] max-w-full"
                                    draggable="false"
                                />
                                {renderMixedSlots()}
                            </div>
                        ) : (
                            <div className="relative w-full h-[45%] flex justify-center items-center">
                                <div className="relative h-full w-full flex justify-center items-center">
                                    <div className="relative h-full" style={{ maxWidth: '100%' }}>
                                        <img
                                            src={quizQuestion.image}
                                            alt="Question"
                                            className="h-full w-auto object-contain max-w-full"
                                            draggable="false"
                                        />
                                        {renderMixedSlots()}
                                    </div>
                                </div>
                            </div>
                        );
                    })()
                ) : null}

                {quizQuestion.questionType === 'Drag-And-Drop-Question' && (
                    <div className="flex">
                        <div className="mt-2 flex flex-wrap justify-center gap-2 z-20">
                            {shuffledLabels.map((label, index) => (
<div
                                key={index}
                                className={`quiz-chip ${Object.values(droppedLabels).includes(label) ? 'quiz-chip-used' : ''}`}
                                onClick={() => handleLabelClick(label)}
                            >
                                {label}
                            </div>
                            ))}
                        </div>
                        <div className="mt-2 flex mx-2">
                            {buttonsAreClickable && Object.keys(droppedLabels).length > 0 && (
<button
                                type="button"
                                onClick={handleReset}
                                className="quiz-secondary-btn focus:outline-none"
                            >
                                Reset Labels
                            </button>
                        )}
                            {buttonsAreClickable && shuffledLabels.length === 0 && (
                                <button
                                    type="button"
                                    onClick={handleDragBoxSubmit}
                                    className="quiz-submit-btn focus:outline-none"
                                >
                                    Submit
                                </button>
                            )}

                        </div>
                    </div>
               )}
                {quizQuestion.questionType === 'Blank-Box-Question' && buttonsAreClickable && (
                    <button
                        type="button"
                        onClick={handleBlankBoxSubmit}
                        className="quiz-submit-btn focus:outline-none"
                    >
                        Submit
                    </button>
                )}
            </div>
        </div>
        {hoveredBox &&
            createPortal(
                <div
                    className="tutor-box-hover-popover"
                    style={{
                        position: 'fixed',
                        top: hoveredBox.top - 8,
                        left: hoveredBox.left + hoveredBox.width / 2,
                        transform: 'translate(-50%, -100%)',
                        zIndex: 100000,
                        pointerEvents: 'none',
                    }}
                >
                    {hoveredBox.options && hoveredBox.options.length > 0 ? (
                        <ul
                            className="tutor-box-hover-popover-answer"
                            style={{ listStyle: 'none', margin: 0, padding: 0, textAlign: 'left' }}
                        >
                            {hoveredBox.options.map((opt) => {
                                const isStudent = opt.id === hoveredBox.studentOptionId;
                                const isCorrect = opt.id === hoveredBox.correctOptionId;
                                return (
                                    <li
                                        key={opt.id}
                                        style={{
                                            padding: '2px 0',
                                            color: isCorrect ? '#059669' : '#333',
                                            fontWeight: isStudent ? 600 : 400,
                                        }}
                                    >
                                        <span style={{ display: 'inline-block', width: 12, textAlign: 'center' }}>
                                            {isStudent ? '•' : ''}
                                        </span>
                                        {opt.text}
                                        {isCorrect && (
                                            <span style={{ marginLeft: 6, fontSize: '0.85em' }}>✓</span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <span className="tutor-box-hover-popover-answer">
                            {hoveredBox.studentAnswer !== undefined && hoveredBox.studentAnswer !== '' ? hoveredBox.studentAnswer : '—'}
                        </span>
                    )}
                </div>,
                document.body
            )}
        </>
    );
}

export default TutorSingleQuestionPage;